<!-- Patient_Programs.vue — All patient opportunities grouped by category -->
<template>
  <div>
    <!-- Task Detail View -->
    <div v-if="selectedTaskId" class="h-full">
      <Patient_Task
        :task-id="selectedTaskId"
        @close="closeTask"
        @submit="onTaskSubmit"
        @update_row="$emit('task-updated')"
        @show-message="$emit('show-message', $event)"
      />
    </div>

    <!-- Programs List View -->
    <div v-else class="space-y-6">
    <!-- Show completed toggle -->
    <div class="flex justify-end px-1">
      <label class="flex items-center gap-1.5 text-[10px] text-gray-400 cursor-pointer select-none">
        <input type="checkbox" v-model="showCompleted" @change="loadData" class="rounded border-gray-300 text-green-500 focus:ring-green-400 w-3 h-3" />
        Show completed & expired
      </label>
    </div>
    <!-- Loading -->
    <div v-if="loading" class="space-y-6">
      <div v-for="g in 2" :key="'sk-group-' + g" class="space-y-3">
        <div class="flex items-center justify-between">
          <div class="h-3 w-24 bg-gray-200 rounded animate-pulse"></div>
          <div class="h-3 w-16 bg-gray-100 rounded animate-pulse"></div>
        </div>
        <div v-for="n in (g === 1 ? 2 : 1)" :key="'sk-prog-' + n" class="bg-white border border-gray-200 rounded-lg">
          <div class="px-4 py-3 bg-gray-50 border-b border-gray-100 flex items-center justify-between">
            <div class="flex items-center gap-2">
              <div class="h-5 w-10 bg-gray-200 rounded animate-pulse"></div>
              <div class="space-y-1">
                <div class="h-3 w-28 bg-gray-200 rounded animate-pulse"></div>
                <div class="h-2.5 w-20 bg-gray-100 rounded animate-pulse"></div>
              </div>
            </div>
            <div class="h-3 w-14 bg-gray-100 rounded animate-pulse"></div>
          </div>
          <div class="px-4 py-3 space-y-2">
            <div v-for="t in 2" :key="'sk-task-' + t" class="flex items-center gap-2">
              <div class="w-5 h-5 rounded-full bg-gray-200 animate-pulse shrink-0"></div>
              <div class="h-3 bg-gray-100 rounded animate-pulse" :style="{ width: t === 1 ? '60%' : '45%' }"></div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Empty -->
    <div v-else-if="!groupedPrograms.length" class="text-center py-8">
      <p class="text-xs text-gray-400">No opportunities for this patient</p>
    </div>

    <!-- Grouped opportunity cards -->
    <template v-else>
      <div v-for="group in groupedPrograms" :key="group.category" class="space-y-3">
        <!-- Category header -->
        <div class="flex items-center justify-between">
          <h4 class="text-xs font-semibold text-gray-500 uppercase tracking-wider">{{ group.category }}</h4>
          <span class="text-xs text-gray-400">{{ group.programs.length }} {{ group.programs.length === 1 ? 'opportunity' : 'opportunities' }}</span>
        </div>

        <!-- Program cards -->
        <div
          v-for="program in group.programs"
          :key="program.opportunity_id"
          :class="[
            'border rounded-lg',
            program.expired_at ? 'bg-gray-50 border-gray-200 opacity-75' :
            program.completed_at ? 'bg-gray-50 border-gray-200' :
            'bg-white border-gray-200'
          ]"
        >
          <!-- Program header -->
          <div class="px-4 py-3 bg-gray-50 border-b border-gray-100 flex items-center justify-between">
            <div class="flex items-center gap-2 min-w-0">
              <span
                class="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold border shrink-0"
                :style="programBadgeStyle(program)"
              >{{ programAbbrev(program.metric_name) }}</span>
              <div class="flex flex-col min-w-0">
                <span class="text-xs font-semibold text-gray-900 truncate">{{ program.metric_name }}</span>
                <span
                  v-if="program.provider_name"
                  class="text-[10px] text-gray-500 truncate sensitive flex items-center gap-1"
                  :title="`Provider: ${program.provider_name}`"
                >
                  <FontAwesomeIcon :icon="['fas', 'user-doctor']" class="text-[9px] text-gray-400" />
                  {{ titleCaseName(program.provider_name) }}
                </span>
              </div>
            </div>
            <div class="flex items-center gap-3 text-xs text-gray-500">
              <span v-if="program.expired_at" class="text-gray-400 font-medium">Expired</span>
              <span v-else-if="program.completed_at && !program.active_task_count" class="text-green-600 font-medium">Completed</span>
              <span v-if="program.active_task_count" class="text-blue-600 font-medium">
                {{ program.active_task_count }} active
              </span>
              <span v-if="program.inactive_task_count" class="text-gray-400">
                {{ program.inactive_task_count }} completed
              </span>
              <!-- Admin: Restart workflow -->
              <button
                v-if="isAdmin"
                @click="restartConfirmId = program.opportunity_id"
                title="Restart this program from the first step"
                class="text-red-500 hover:text-red-700 font-medium transition-colors"
              >
                Restart
              </button>
            </div>
          </div>

          <!-- Admin restart confirmation -->
          <div
            v-if="isAdmin && restartConfirmId === program.opportunity_id"
            class="px-4 py-3 bg-red-50 border-b border-red-100"
          >
            <p class="text-xs text-red-800 mb-2">
              Delete this program's current task(s) and start over from the first step?
              Logged work is preserved. This cannot be undone.
            </p>
            <div class="flex items-center gap-2">
              <button
                @click="restartProgram(program)"
                :disabled="restarting"
                class="px-3 py-1.5 text-xs font-medium bg-red-600 text-white rounded
                       hover:bg-red-700 disabled:opacity-50"
              >
                {{ restarting ? 'Restarting…' : 'Confirm Restart' }}
              </button>
              <button
                @click="cancelRestart"
                :disabled="restarting"
                class="px-3 py-1.5 text-xs font-medium bg-white border border-gray-300
                       text-gray-700 rounded hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>

          <!-- Index-event details (post-discharge / TOC opportunities) -->
          <div
            v-if="program.index_event && indexEventFields(program.index_event).length"
            class="px-4 py-2.5 bg-blue-50/40 border-b border-gray-100"
          >
            <div class="flex flex-wrap items-center justify-between gap-2 mb-2 pb-2 border-b border-blue-100/60" v-if="program.index_event.cadence_stage">
              <div class="flex items-center gap-2">
                <span class="text-[10px] uppercase font-bold tracking-wider text-gray-500">Call Cadence:</span>
                <span
                  class="px-2 py-0.5 rounded text-xs font-semibold"
                  :class="cadenceBadgeClass(program.index_event.cadence_stage)"
                >
                  {{ cadenceStageLabel(program.index_event.cadence_stage) }}
                </span>
              </div>
              <div class="text-xs text-gray-600 flex items-center gap-3">
                <span v-if="program.index_event.days_since_last_ed !== null && program.index_event.days_since_last_ed !== undefined">
                  <strong class="text-gray-900">{{ program.index_event.days_since_last_ed }}</strong> days since last ED
                </span>
                <span v-if="program.index_event.num_ed_visits" class="text-gray-400">·</span>
                <span v-if="program.index_event.num_ed_visits">
                  <strong class="text-gray-900">{{ program.index_event.num_ed_visits }}</strong> visits (6mo)
                </span>
              </div>
            </div>
            <div class="flex flex-wrap gap-x-5 gap-y-1.5">
              <div
                v-for="field in indexEventFields(program.index_event)"
                :key="field.label"
                class="flex flex-col min-w-0"
              >
                <span class="text-[9px] uppercase tracking-wide text-gray-400">{{ field.label }}</span>
                <span class="text-xs font-medium text-gray-800 truncate" :title="field.value">{{ field.value }}</span>
              </div>
            </div>
          </div>

          <!-- Patient dossiers — the async SAT results on this opportunity, one
               collapsible block each, COLLAPSED by default.

               DRIVEN BY THE METRIC'S OWN BINDINGS, not a hardcoded key. This used
               to read program.adt_assessment directly, so IHCI's ed_utilization
               dossier was generated nightly and rendered nowhere: the data reached
               opp.metadata and stopped at the panel. The API now returns every
               declared dossier as program.dossiers[], so a new binding needs no
               change here.

               Falls back to the legacy adt_assessment field for any caller still
               emitting only that. -->
          <div
            v-for="d in programDossiers(program)"
            :key="d.key"
            class="border-b border-gray-100"
          >
            <button
              type="button"
              class="w-full px-4 py-2.5 flex items-center gap-2 bg-teal-50 hover:bg-teal-100 transition-colors border-l-2 border-teal-400"
              @click="toggleDossier(program.opportunity_id, d.key)"
            >
              <font-awesome-icon :icon="['fas', dossierIcon(d.key)]" class="text-xs text-teal-600" />
              <span class="text-xs font-semibold text-teal-800">{{ d.label }}</span>
              <span class="text-[11px] font-medium text-teal-600 ml-auto flex items-center gap-1">
                {{ isDossierOpen(program.opportunity_id, d.key) ? 'Hide' : `View ${d.facts.length} details` }}
                <font-awesome-icon
                  :icon="['fas', isDossierOpen(program.opportunity_id, d.key) ? 'chevron-up' : 'chevron-down']"
                  class="text-[10px]"
                />
              </span>
            </button>
            <div v-if="isDossierOpen(program.opportunity_id, d.key)" class="px-4 py-2.5 space-y-1 bg-teal-50/40">
              <div v-for="(f, i) in d.facts" :key="i" class="text-xs text-gray-700 leading-snug">
                <span class="font-semibold mr-1">{{ f.label }}:</span>{{ f.value }}
              </div>
              <!-- Generated-at matters on a dossier that decays: an ED utilization
                   summary leads with "days since last ED visit", which is wrong the
                   day after it is written. -->
              <div v-if="d.generated_at" class="text-[10px] text-gray-400 pt-1">
                as of {{ shortDateTime(d.generated_at) }}
              </div>
            </div>
          </div>

          <!-- Task list: all tasks in chronological order with stage labels -->
          <div class="divide-y divide-gray-100">
            <div
              v-for="task in orderedTasks(program)"
              :key="task.id"
              @click="openTask(task)"
              class="px-4 py-2.5 flex items-center justify-between cursor-pointer hover:bg-gray-50 transition-colors group"
            >
              <div class="flex items-center gap-3 flex-1 min-w-0">
                <div :class="[
                  'w-2 h-2 rounded-full shrink-0',
                  task.is_inactive && task.completed_at ? 'bg-green-500' :
                  task.is_inactive && task.expired_at ? 'bg-gray-400' :
                  task.is_overdue ? 'bg-red-500' :
                  task.status.id === TaskStatus.SNOOZED ? 'bg-yellow-500' :
                  'bg-blue-500'
                ]"></div>
                <div class="flex-1 min-w-0">
                  <div class="flex items-center gap-2">
                    <span class="text-xs font-medium text-gray-900">{{ task.task_type?.name || '' }} ({{ (task.call_attempts || 0) + 1 }})</span>
                    <!-- Work badges -->
                    <div v-if="(task.work_completed || []).length" class="flex items-center -space-x-0.5">
                      <div
                        v-for="(work, widx) in (task.work_completed || []).slice(0, 5)"
                        :key="work.id || widx"
                        class="w-4 h-4 rounded-full flex items-center justify-center text-white text-[8px] border border-white"
                        :class="workDotColor(work.work_type_name)"
                        :title="workTooltip(work)"
                      ><FontAwesomeIcon :icon="workIcon(work.work_type_name)" /></div>
                      <span
                        v-if="(task.work_completed || []).length > 5"
                        class="text-[9px] text-gray-400 ml-1"
                      >+{{ (task.work_completed || []).length - 5 }}</span>
                    </div>
                    <span v-if="task.status.id === TaskStatus.SNOOZED" class="text-[10px] px-1.5 py-0.5 rounded bg-yellow-100 text-yellow-700 font-medium">Snoozed</span>
                    <span v-if="task.is_overdue && !task.is_inactive" class="text-[10px] px-1.5 py-0.5 rounded bg-red-100 text-red-700 font-medium">Overdue</span>
                  </div>
                  <div class="flex items-center gap-3 text-xs text-gray-500 mt-0.5">
                    <span v-if="task.due_date">{{ formatDate(task.due_date) }}<template v-if="task.due_time"> at {{ formatDueTime(task.due_time, task.due_date) }}</template></span>
                    <span v-if="task.owner_name" class="sensitive">{{ task.owner_name }}</span>
                    <span v-else-if="!task.is_inactive" class="text-amber-600">Unassigned</span>
                    <span v-if="task.is_inactive && task.completed_at" class="text-green-600">Completed {{ formatRelative(task.completed_at) }}</span>
                    <span v-if="task.is_inactive && task.expired_at" class="text-gray-400">Expired {{ formatRelative(task.expired_at) }}</span>
                  </div>
                </div>
              </div>
              <!-- Action dropdown for active tasks -->
              <div v-if="task.available_actions && task.available_actions.length && !task.is_inactive" class="flex flex-col items-end gap-1.5 shrink-0" @click.stop>
                <div class="flex items-center gap-2">
                  <select
                    v-model="pendingAction[task.id]"
                    @change="clearActionDate(task.id)"
                    class="text-xs border border-gray-300 rounded px-2 py-1 min-w-[120px] focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="">Action...</option>
                    <option v-for="a in task.available_actions" :key="a.id" :value="a.id">{{ a.name }}</option>
                  </select>
                  <button
                    v-if="pendingAction[task.id]"
                    @click.stop="submitAction(task)"
                    :disabled="submittingAction[task.id] || (needsDate(task) && !actionDates[task.id])"
                    class="text-xs px-2 py-1 rounded bg-primary-500 text-white hover:bg-primary-600 disabled:opacity-50"
                  >{{ submittingAction[task.id] ? '...' : 'Go' }}</button>
                </div>
                <div v-if="pendingAction[task.id] && needsDate(task)" class="flex items-center gap-1">
                  <span class="text-[10px] text-gray-500 mr-0.5">{{ dateLabel(task) }}</span>
                  <button
                    v-for="opt in [{days:1,label:'1d'},{days:3,label:'3d'},{days:7,label:'7d'}]"
                    :key="opt.days"
                    class="px-1.5 py-1 rounded text-[10px] font-medium transition-colors"
                    :class="actionDaysSelected[task.id] === opt.days ? 'bg-gray-900 text-white' : 'bg-white border border-gray-300 text-gray-600 hover:bg-gray-100'"
                    @click="setActionDate(task.id, opt.days)"
                  >{{ opt.label }}</button>
                  <div class="relative">
                    <button
                      class="px-1.5 py-1 border border-gray-300 rounded text-[10px] w-[130px] text-left bg-white text-gray-700 hover:border-gray-400 transition-colors"
                      @click="showCalendarFor = showCalendarFor === task.id ? null : task.id"
                    >{{ actionDates[task.id] ? formatPickerDisplay(actionDates[task.id]) : 'Pick a date...' }}</button>
                    <div
                      v-if="showCalendarFor === task.id"
                      class="absolute top-full right-0 mt-1 z-50 shadow-lg rounded-lg"
                      style="width: 280px;"
                    >
                      <TaskCalendarPicker
                        :model-value="actionDates[task.id]"
                        @update:model-value="onCalendarPick(task.id, $event)"
                        @day-picked="showCalendarFor = null"
                      />
                    </div>
                  </div>
                </div>
              </div>
              <FontAwesomeIcon v-else :icon="['fas', 'chevron-right']" class="text-gray-300 group-hover:text-gray-500 text-xs" />
            </div>
            <div v-if="!orderedTasks(program).length" class="px-4 py-3 text-xs text-gray-400 text-center">No tasks</div>
          </div>
        </div>
      </div>
    </template>

    <!-- Action buttons -->
    <div class="flex items-center justify-center gap-2 py-4">
      <button @click="loadData()" class="text-xs px-3 py-1.5 rounded border border-gray-200 bg-white text-gray-600 hover:bg-gray-100 shadow-sm flex items-center gap-1.5">
        <FontAwesomeIcon :icon="['fas', 'arrows-rotate']" :class="loading ? 'animate-spin' : ''" />
        Refresh
      </button>
      <div class="relative" v-if="!enrolling">
        <button
          @click="showEnrollMenu = !showEnrollMenu"
          class="text-xs px-3 py-1.5 rounded border border-gray-200 bg-white text-gray-700 hover:bg-gray-100 shadow-sm flex items-center gap-1.5"
        >
          <FontAwesomeIcon :icon="['fas', 'plus']" class="text-[9px]" />
          Enroll in Program
        </button>
        <div v-if="showEnrollMenu" class="absolute left-1/2 -translate-x-1/2 bottom-full mb-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 py-1 w-56 max-h-48 overflow-y-auto">
          <button
            v-for="prog in enrollablePrograms"
            :key="prog.slug"
            @click="openEnrollmentConfirmation(prog)"
            class="w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 text-gray-700"
          >{{ prog.name }}</button>
        </div>
      </div>
      <span v-if="enrolling" class="text-xs text-gray-400">
        <FontAwesomeIcon :icon="['fas', 'spinner']" class="animate-spin" /> Enrolling...
      </span>
    </div>

    <teleport to="body">
      <div
        v-if="selectedEnrollProgram"
        class="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
        @click.self="cancelEnrollment"
      >
        <div class="w-full max-w-md rounded-lg bg-white shadow-xl">
          <div class="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <h3 class="text-sm font-semibold text-gray-900">Enroll in Program</h3>
            <button :disabled="enrolling" @click="cancelEnrollment" class="text-gray-400 hover:text-gray-600 text-lg leading-none disabled:opacity-50">&times;</button>
          </div>
          <div class="p-4 space-y-3">
            <p class="text-xs text-gray-600">Enroll this patient in <span class="font-semibold text-gray-900">{{ selectedEnrollProgram.name }}</span>.</p>
            <div>
              <label class="block text-xs font-medium text-gray-700 mb-1">Initial Due Date <span class="text-red-500">*</span></label>
              <div>
                <button ref="enrollDueDateTrigger" type="button" @click="toggleEnrollDueCalendar"
                  class="w-full px-2 py-1.5 text-left text-xs border border-gray-300 rounded-md bg-white hover:border-primary-400 focus:ring-1 focus:ring-primary-500">
                  {{ enrollDueDate || 'Select initial due date…' }}
                </button>
              </div>
              <p class="mt-1 text-xs text-gray-400">Weekend and holiday dates will be moved to the next business day.</p>
            </div>
            <p v-if="enrollError" class="text-xs text-red-600">{{ enrollError }}</p>
          </div>
          <div class="flex items-center justify-end gap-2 px-4 py-3 border-t border-gray-100">
            <button :disabled="enrolling" @click="cancelEnrollment" class="px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 rounded hover:bg-gray-200 disabled:opacity-50">Cancel</button>
            <button :disabled="enrolling || !enrollDueDate" @click="confirmEnrollment" class="px-3 py-1.5 text-xs font-medium text-white bg-primary-600 rounded hover:bg-primary-700 disabled:opacity-50">{{ enrolling ? 'Enrolling…' : 'Enroll' }}</button>
          </div>
        </div>
      </div>
    </teleport>

    <teleport to="body">
      <div v-if="showEnrollDueCalendar" class="fixed inset-0 z-[60]" @click.self="showEnrollDueCalendar = false">
        <div class="fixed z-[61] max-h-[calc(100vh-1rem)] overflow-y-auto shadow-lg" :style="enrollDueCalendarPosition">
          <TaskCalendarPicker
            :model-value="enrollDueDate"
            date-only
            @update:model-value="enrollDueDate = $event"
            @day-picked="showEnrollDueCalendar = false"
          />
        </div>
      </div>
    </teleport>
    </div>
  </div>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { mapGetters } from 'vuex';
import Patient_Task from './Patient_Task.vue';
import TaskCalendarPicker from '@/components/shared/TaskCalendarPicker.vue';
import { submitAction } from '@/services/api/tasks';
import { checkEnrollment, enrollPatient } from '@/services/api/opportunities';
import { listCareManagementPrograms } from '@/services/api/careManagementPrograms';
import { InstructionType, TaskStatus } from '@/constants/instructionTypes';
import { formatDueTime, loadBusinessDayHolidays, nextBusinessDay } from '@/utils/common';
import { POSTHOG_FLAGS } from '@/constants/posthogFlags';
import { isPostHogFlagEnabled } from '@/utils/posthogFlags';
import abortable from '@/mixins/abortable';

const CATEGORY_ORDER = ['Care Management', 'Coding Accuracy', 'Quality'];

export default {
  name: 'Patient_Programs',
  components: { FontAwesomeIcon, Patient_Task, TaskCalendarPicker },
  mixins: [abortable],
  props: {
    patientIdentifier: { type: String, required: true },
    entityId: { type: Number, default: null },
    initialTaskId: { type: [Number, String], default: null },
    metricCategory: { type: String, default: null }
  },
  emits: ['show-message', 'open-opportunity', 'task-updated'],

  data() {
    return {
      TaskStatus,
      loading: false,
      programs: [],
      selectedTaskId: null,
      showCompleted: false,
      pendingAction: {},
      actionDates: {},
      actionDaysSelected: {},
      showCalendarFor: null,
      submittingAction: {},
      showEnrollMenu: false,
      enrolling: false,
      selectedEnrollProgram: null,
      enrollDueDate: '',
      showEnrollDueCalendar: false,
      enrollDueCalendarPosition: {},
      enrollError: '',
      allCmPrograms: [],
      restartConfirmId: null,
      adtOpen: {},  // `${opportunity_id}:${store_key}` -> bool: is that dossier block expanded
      restarting: false,
    };
  },

  computed: {
    ...mapGetters('auth', ['current_user']),
    ...mapGetters('tenant', ['isTenantAdmin']),
    isAdmin() {
      return this.isTenantAdmin || !!this.current_user?.is_superuser;
    },
    showStatusDropdown() {
      return isPostHogFlagEnabled(POSTHOG_FLAGS.SHOW_OPPORTUNITY_STATUS_DROPDOWN);
    },
    groupedPrograms() {
      const groups = {};
      for (const p of this.programs) {
        const cat = p.metric_category || 'Quality';
        if (!groups[cat]) groups[cat] = [];
        groups[cat].push(p);
      }
      return CATEGORY_ORDER
        .filter(cat => groups[cat])
        .map(cat => ({ category: cat, programs: groups[cat] }));
    },
    enrollablePrograms() {
      const enrolledSlugs = new Set(
        this.programs
          .filter(p => !p.completed_at && !p.expired_at)
          .map(p => p.metric_slug)
      );
      return this.allCmPrograms.filter(p => !enrolledSlugs.has(p.slug));
    }
  },

  mounted() {
    // Warm the shared holiday cache for the quick-fill buttons (see
    // setActionDate). Not awaited — falls back to weekends-only if pending.
    loadBusinessDayHolidays(this.$axios);
    document.addEventListener('click', this._closeEnrollMenu);
  },
  beforeUnmount() {
    document.removeEventListener('click', this._closeEnrollMenu);
  },

  watch: {
    patientIdentifier: {
      immediate: true,
      handler(val) {
        this.selectedTaskId = null;
        if (val) {
          const signal = this._renewAbortSignal();
          this.loadData(signal);
        }
      }
    },
    initialTaskId(val) {
      this.selectedTaskId = val || null;
    }
  },

  methods: {
    positionEnrollDueCalendar() {
      const trigger = this.$refs.enrollDueDateTrigger;
      if (!trigger) return;
      const rect = trigger.getBoundingClientRect();
      const margin = 8;
      const width = Math.min(320, window.innerWidth - margin * 2);
      const height = 360;
      const left = Math.min(Math.max(margin, rect.left), window.innerWidth - width - margin);
      const below = rect.bottom + margin + height <= window.innerHeight;
      const top = below ? rect.bottom + margin : Math.max(margin, rect.top - height - margin);
      this.enrollDueCalendarPosition = { left: `${left}px`, top: `${top}px`, width: `${width}px` };
    },
    toggleEnrollDueCalendar() {
      if (this.showEnrollDueCalendar) {
        this.showEnrollDueCalendar = false;
        return;
      }
      this.positionEnrollDueCalendar();
      this.showEnrollDueCalendar = true;
    },
    openTask(task) {
      this.selectedTaskId = task.id;
    },
    closeTask() {
      this.selectedTaskId = null;
    },
    cancelRestart() {
      this.restartConfirmId = null;
    },
    async restartProgram(program) {
      this.restarting = true;
      try {
        await this.$axios.post(`/api/opportunities/${program.opportunity_id}/restart/`, {});
        this.cancelRestart();
        this.selectedTaskId = null;
        this.$emit('task-updated');
        this.loadData();
        this.$emit('show-message', { text: 'Program restarted from the first step', type: 'success' });
      } catch (err) {
        this.$emit('show-message', { text: err.response?.data?.error || 'Failed to restart program', type: 'error' });
      } finally {
        this.restarting = false;
      }
    },
    onTaskSubmit() {
      this.selectedTaskId = null;
      this.$emit('task-updated');
      this.loadData();
    },
    clearActionDate(taskId) {
      this.actionDates = { ...this.actionDates, [taskId]: '' };
      this.actionDaysSelected = { ...this.actionDaysSelected, [taskId]: null };
      this.showCalendarFor = null;
    },
    // Quick-fill (1d/2d/3d/7d). Lands on the next BUSINESS day so the date
    // shown matches the date the backend stores — same fix as
    // ClinicalEngagement.setActionDate. See nextBusinessDay in utils/common.js
    // and IHCI #2587. Both copies must stay in step.
    setActionDate(taskId, days) {
      this.actionDaysSelected = { ...this.actionDaysSelected, [taskId]: days };
      const d = new Date();
      d.setDate(d.getDate() + days);
      const b = nextBusinessDay(d);
      b.setHours(9, 0, 0, 0);
      const pad = n => String(n).padStart(2, '0');
      this.actionDates = {
        ...this.actionDates,
        [taskId]: `${b.getFullYear()}-${pad(b.getMonth() + 1)}-${pad(b.getDate())}T${pad(b.getHours())}:${pad(b.getMinutes())}`,
      };
    },
    onCalendarPick(taskId, val) {
      this.actionDates = { ...this.actionDates, [taskId]: val };
      this.actionDaysSelected = { ...this.actionDaysSelected, [taskId]: null };
    },
    formatPickerDisplay(val) {
      if (!val) return '';
      try {
        return new Date(val).toLocaleString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true });
      } catch { return val; }
    },
    needsDate(task) {
      const actionId = this.pendingAction[task.id];
      if (!actionId) return false;
      const action = task.available_actions.find(a => a.id === actionId);
      return action && [InstructionType.SNOOZE, InstructionType.UPDATE_DUE_DATE].includes(action.task_instruction_type_id);
    },
    dateLabel(task) {
      const actionId = this.pendingAction[task.id];
      const action = task.available_actions.find(a => a.id === actionId);
      if (action?.task_instruction_type_id === InstructionType.SNOOZE) return 'Snooze until';
      if (action?.task_instruction_type_id === InstructionType.UPDATE_DUE_DATE) return 'New due date';
      return 'Select date';
    },
    async submitAction(task) {
      const actionTypeId = this.pendingAction[task.id];
      if (!actionTypeId) return;
      this.submittingAction = { ...this.submittingAction, [task.id]: true };
      try {
        const currentUser = this.$store?.getters['auth/current_user'];
        const action = task.available_actions.find(a => a.id === actionTypeId);
        const metadata = {};
        if (action?.task_instruction_type_id === InstructionType.SNOOZE) {
          metadata.snooze_until = new Date(this.actionDates[task.id]).toISOString();
        }
        if (action?.task_instruction_type_id === InstructionType.UPDATE_DUE_DATE) {
          metadata.due_date = new Date(this.actionDates[task.id]).toISOString();
        }
        const result = await submitAction({
          task_id: task.id,
          task_action_type_id: actionTypeId,
          metadata,
        });
        if (result.already_completed) {
          this.$emit('show-message', { type: 'warning', text: 'Task was already completed' });
        }
        this.pendingAction = { ...this.pendingAction, [task.id]: '' };
        this.actionDates = { ...this.actionDates, [task.id]: '' };
        this.actionDaysSelected = { ...this.actionDaysSelected, [task.id]: null };
        this.showCalendarFor = null;
        this.$emit('task-updated');
        this.loadData();
      } catch (e) {
        console.error('Action failed:', e);
        this.$emit('show-message', { type: 'error', text: 'Action failed' });
      } finally {
        this.submittingAction = { ...this.submittingAction, [task.id]: false };
      }
    },
    _closeEnrollMenu(e) {
      if (this.showEnrollMenu && !e.target.closest('.relative')) {
        this.showEnrollMenu = false;
      }
    },
    openEnrollmentConfirmation(prog) {
      this.showEnrollMenu = false;
      this.selectedEnrollProgram = prog;
      this.enrollDueDate = '';
      this.showEnrollDueCalendar = false;
      this.enrollError = '';
    },
    cancelEnrollment() {
      if (this.enrolling) return;
      this.selectedEnrollProgram = null;
      this.enrollDueDate = '';
      this.showEnrollDueCalendar = false;
      this.enrollError = '';
    },
    async confirmEnrollment() {
      if (!this.selectedEnrollProgram || !this.enrollDueDate || this.enrolling) return;
      await this.enrollInProgram(this.selectedEnrollProgram);
    },
    async enrollInProgram(prog) {
      this.enrolling = true;
      this.enrollError = '';
      try {
        const payload = {
          patient_identifier: this.patientIdentifier,
          metric_id: prog.id,
          entity_id: this.entityId,
          first_task_due_date: this.enrollDueDate,
          metadata: {}
        };
        if (prog.subtype) {
          payload.metadata.referral_type = prog.subtype;
        }
        await enrollPatient(payload);
        this.$root.$emit('show-success-toast', `Enrolled in ${prog.name}. Computing priority flags...`);
        this.$emit('task-updated');
        this.loadData();
        this.selectedEnrollProgram = null;
        this.enrollDueDate = '';
        this.showEnrollDueCalendar = false;
      } catch (e) {
        const msg = e.response?.data?.message || 'Enrollment failed';
        this.enrollError = msg;
      } finally {
        this.enrolling = false;
      }
    },
    async loadData(maybeSignal) {
      const signal = maybeSignal instanceof AbortSignal ? maybeSignal : undefined;
      if (!this.patientIdentifier) return;
      this.loading = true;
      const enrollmentPayload = {
        patient_identifier: this.patientIdentifier,
        entity_id: this.entityId,
        show_completed: this.showCompleted,
      };
      try {
        const [enrollment, programsResponse] = await Promise.all([
          checkEnrollment(enrollmentPayload, { signal }),
          this.allCmPrograms.length ? Promise.resolve(null) : listCareManagementPrograms({ signal })
        ]);
        const programs = enrollment.enrolled_programs || [];
        this.programs = this.metricCategory
          ? programs.filter(program => program.metric_category === this.metricCategory)
          : programs;
        if (programsResponse) this.allCmPrograms = programsResponse;
      } catch (error) {
        if (this._isAborted(error)) return;
        console.error('Error loading programs:', error);
        this.programs = [];
        this.$emit('show-message', { text: 'Failed to load programs', type: 'error' });
      } finally {
        if (!signal?.aborted) this.loading = false;
      }
    },

    formatEventDate(raw) {
      // Index-event dates arrive in mixed shapes: ISO with T, date-only
      // (YYYY-MM-DD), or space-separated datetime with millis
      // (e.g. "2026-06-26 14:03:00.000"). Normalize, then show date only.
      if (!raw) return null;
      let s = String(raw).trim();
      // date-only → pin to local midnight (avoid UTC shift)
      if (/^\d{4}-\d{2}-\d{2}$/.test(s)) s += 'T00:00:00';
      // space-separated datetime → ISO 'T' separator
      else if (s.includes(' ')) s = s.replace(' ', 'T');
      const d = new Date(s);
      if (isNaN(d.getTime())) return null;
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    },

    toggleDossier(oppId, key) {
      const k = `${oppId}:${key}`;
      this.adtOpen = { ...this.adtOpen, [k]: !this.adtOpen[k] };
    },
    isDossierOpen(oppId, key) {
      return !!this.adtOpen[`${oppId}:${key}`];
    },
    programDossiers(program) {
      // Every dossier the API returned for this opportunity, already filtered to
      // non-empty facts server-side. Falls back to the legacy single
      // adt_assessment field so a stale API build still renders it.
      const list = program?.dossiers;
      if (Array.isArray(list) && list.length) return list;
      const facts = program?.adt_assessment?.facts;
      if (!Array.isArray(facts)) return [];
      const clean = facts.filter(
        f => f && f.label && f.value !== undefined && f.value !== null && f.value !== '',
      );
      if (!clean.length) return [];
      return [{
        key: 'adt_assessment',
        label: 'ADT Episode Summary',
        generated_at: program?.adt_assessment_generated_at || null,
        facts: clean,
      }];
    },
    dossierIcon(key) {
      // Icons are per store_key rather than per SAT id so a cloned SAT in another
      // tenant picks up the right one without a mapping table.
      if (key === 'ed_utilization') return 'chart-line';
      return 'hospital';
    },
    shortDateTime(iso) {
      if (!iso) return '';
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return '';
      return d.toLocaleString(undefined, {
        month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
      });
    },
    indexEventFields(ev) {
      // Build an ordered list of present index-event fields for the card header.
      if (!ev) return [];
      const out = [];
      const push = (label, value) => {
        if (value === null || value === undefined || value === '') return;
        out.push({ label, value });
      };
      const titleCase = (s) => typeof s === 'string'
        ? s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
        : s;
      push('Facility', ev.facility);
      push('Admit', this.formatEventDate(ev.admit_date));
      if (ev.admit_type) push('Admit Type', titleCase(ev.admit_type));
      push('Discharge', this.formatEventDate(ev.discharge_date));
      push('Disposition', titleCase(ev.disposition));
      if (ev.lace_score !== null && ev.lace_score !== undefined) push('LACE', ev.lace_score);
      if (ev.ed_risk_score !== null && ev.ed_risk_score !== undefined) push('Risk Score', ev.ed_risk_score);
      push('Discharge Dx', ev.discharge_diagnosis);
      push('Episode', titleCase(ev.episode_type));
      push('Service Area', ev.service_area);
      if (ev.tcm_eligible === true) push('TCM', 'Eligible');
      return out;
    },

    cadenceStageLabel(stage) {
      const map = {
        'WEEKLY': 'Weekly Outreach',
        'BIWEEKLY': 'Biweekly Calls (2+ mo no ED)',
        'MONTHLY': 'Monthly Calls (4+ mo no ED)',
        'GRADUATE_ELIGIBLE': 'Graduation Review (6+ mo no ED)',
      };
      return map[stage] || stage;
    },

    cadenceBadgeClass(stage) {
      const map = {
        'WEEKLY': 'bg-blue-100 text-blue-800 border border-blue-200',
        'BIWEEKLY': 'bg-amber-100 text-amber-800 border border-amber-200',
        'MONTHLY': 'bg-purple-100 text-purple-800 border border-purple-200',
        'GRADUATE_ELIGIBLE': 'bg-green-100 text-green-800 border border-green-200',
      };
      return map[stage] || 'bg-gray-100 text-gray-800 border border-gray-200';
    },

    orderedTasks(program) {
      // All tasks for this program, sorted chronologically by activation
      return [...(program.tasks || [])].sort((a, b) => {
        const aTime = a.activated_at || a.due_date || '';
        const bTime = b.activated_at || b.due_date || '';
        return aTime < bTime ? -1 : aTime > bTime ? 1 : 0;
      });
    },

    workIcon(typeName) {
      const map = {
        'clinical_engagement': ['fas', 'phone'],
        'phone_call': ['fas', 'phone'],
        'care_gap_patient_call': ['fas', 'phone'],
        'PHQ9': ['fas', 'clipboard-list'],
        'GAD7': ['fas', 'clipboard-list'],
        'comment': ['fas', 'comment'],
        'patient_call': ['fas', 'phone'],
      };
      return map[typeName] || ['fas', 'check'];
    },
    workDotColor(typeName) {
      const map = {
        'clinical_engagement': 'bg-primary-500',
        'care_gap_patient_call': 'bg-blue-500',
        'phone_call': 'bg-green-500',
        'PHQ9': 'bg-purple-500',
        'GAD7': 'bg-purple-500',
        'comment': 'bg-gray-400',
        'patient_call': 'bg-green-500',
      };
      return map[typeName] || 'bg-gray-500';
    },
    workTooltip(work) {
      const name = work.work_type_display_name || work.work_type_name || 'Work';
      const date = work.created_at ? new Date(work.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';
      const user = work.appuser_name || '';
      return [name, user, date].filter(Boolean).join(' · ');
    },
    programBadgeStyle(program) {
      const color = program.metric_uicolor || '#6b7280';
      return {
        backgroundColor: color + '15',
        color: color,
        borderColor: color + '40'
      };
    },

    titleCaseName(name) {
      // Provider names on entities are commonly stored uppercase
      // (e.g. "JANE SMITH D O"). Title-case for display.
      if (!name) return '';
      return String(name)
        .toLowerCase()
        .replace(/\b\w/g, c => c.toUpperCase());
    },

    programAbbrev(name) {
      if (!name) return '?';
      if ((name.includes('VytalCare') || name.includes('Vytal Care')) && name.includes('TOC')) return 'VC+ TOC';
      if (name.includes('Transitions')) return 'TOC';
      if (name.includes('VytalCare') || name.includes('Vytal Care')) return 'VC+';
      if (name.includes('Chronic Disease')) return 'CDM';
      if (name.includes('SNF')) return 'SNF';
      if (name.includes('Direct Referral')) return 'REF';
      if (name.includes('Suspect')) return 'SUS';
      if (name.includes('Resubmission')) return 'RES';
      if (name.includes('Early Start')) return 'ES';
      if (name.includes('Medication Adherence')) return 'MedAdh';
      return name.split(/\s+/).slice(0, 2).map(w => w[0]).join('').toUpperCase();
    },

    formatDate(dateStr) {
      if (!dateStr) return '';
      // Date-only strings (YYYY-MM-DD) are parsed as UTC by JS — append time to force local
      const d = dateStr.includes('T') ? new Date(dateStr) : new Date(dateStr + 'T00:00:00');
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    },

    formatDueTime,

    formatRelative(dateStr) {
      if (!dateStr) return '';
      const d = new Date(dateStr);
      const now = new Date();
      const diff = Math.floor((now - d) / 86400000);
      if (diff === 0) return 'today';
      if (diff === 1) return 'yesterday';
      if (diff < 7) return `${diff}d ago`;
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
  }
};
</script>
